import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Table, Button, Modal, Form, Select, InputNumber, Input,
  Space, Statistic, Row, Col, Card, Popconfirm, Typography,
  DatePicker, message, Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';

const { Title, Text } = Typography;

const fmt = (n, digits = 2) =>
  n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const fmtCurrency = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FTEForecastPage() {
  const currentUser = useContext(UserContext);
  const role        = currentUser?.Role;
  const isAdmin     = role === 'admin' || role === 'qa_admin';

  const [rows, setRows]           = useState([]);
  const [allTeams, setAllTeams]   = useState([]);
  const [jobs, setJobs]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form]                    = Form.useForm();

  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [selectedTeams, setSelectedTeams] = useState([]);

  const teamOptions = useMemo(() => {
    // qa_admin follows Visible Teams like everyone else; only admin sees all.
    if (role === 'admin') return allTeams.map((t) => ({ label: t.Name, value: t.ID }));
    const vis = currentUser?.VisibleTeams || [];
    return (vis.length ? vis : allTeams).map((t) => ({ label: t.Name, value: t.ID }));
  }, [role, allTeams, currentUser]);

  const jobOptions = useMemo(
    () => jobs.filter((j) => j.IsActive).map((j) => ({ label: j.JobName, value: j.JobID })),
    [jobs],
  );

  useEffect(() => {
    Promise.all([
      api.get('/api/teams', { params: { no_client_users: 1 } }),
      api.get('/api/jobs'),
    ]).then(([t, j]) => {
      setAllTeams(t.data || []);
      setJobs(j.data || []);
    }).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedMonth) params.set('month', selectedMonth);
    selectedTeams.forEach((id) => params.append('team_id', id));
    api.get(`/api/fte-forecast?${params}`)
      .then((r) => setRows(r.data))
      .catch(() => message.error('Failed to load forecast data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [selectedMonth, selectedTeams]);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ AdherencePct: 96 });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      MonthKey:     row.MonthKey,
      TeamID:       row.TeamID,
      JobID:        row.JobID,
      FTE:          row.FTE,
      WeekdayHrs:   row.WeekdayHrs,
      WeekendHrs:   row.WeekendHrs,
      HolidayHrs:   row.HolidayHrs,
      AdherencePct: row.AdherencePct,
      BillableRate: row.BillableRate,
      Notes:        row.Notes,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/fte-forecast/${editing.ForecastID}`, values);
        message.success('Forecast updated');
      } else {
        await api.post('/api/fte-forecast', values);
        message.success('Forecast created');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Save failed';
      message.error(msg.includes('UQ_') ? 'A forecast for that month/team/job already exists' : msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (forecastId) => {
    try {
      await api.delete(`/api/fte-forecast/${forecastId}`);
      message.success('Deleted');
      load();
    } catch {
      message.error('Delete failed');
    }
  };

  const totals = useMemo(() => {
    const fte  = rows.reduce((s, r) => s + (r.FTE || 0), 0);
    const hrs  = rows.reduce((s, r) => s + (r.EstBillableHrs || 0), 0);
    const rev  = rows.reduce((s, r) => s + (r.EstRevenue || 0), 0);
    const hasRev = rows.some((r) => r.BillableRate != null);
    return { fte, hrs, rev, hasRev };
  }, [rows]);

  const columns = [
    {
      title: 'Team',
      dataIndex: 'TeamName',
      key: 'TeamName',
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName),
    },
    {
      title: 'Job',
      dataIndex: 'JobName',
      key: 'JobName',
      sorter: (a, b) => a.JobName.localeCompare(b.JobName),
    },
    {
      title: 'FTE',
      dataIndex: 'FTE',
      key: 'FTE',
      align: 'right',
      width: 80,
      render: (v) => fmt(v),
    },
    {
      title: 'Weekday Hrs',
      dataIndex: 'WeekdayHrs',
      key: 'WeekdayHrs',
      align: 'right',
      width: 110,
      render: (v) => fmt(v),
    },
    {
      title: 'Weekend Hrs',
      dataIndex: 'WeekendHrs',
      key: 'WeekendHrs',
      align: 'right',
      width: 110,
      render: (v) => fmt(v),
    },
    {
      title: 'Holiday Hrs',
      dataIndex: 'HolidayHrs',
      key: 'HolidayHrs',
      align: 'right',
      width: 110,
      render: (v) => fmt(v),
    },
    {
      title: 'Adherence',
      dataIndex: 'AdherencePct',
      key: 'AdherencePct',
      align: 'right',
      width: 100,
      render: (v) => v != null ? `${fmt(v)}%` : '—',
    },
    {
      title: 'Est. Billable Hrs',
      dataIndex: 'EstBillableHrs',
      key: 'EstBillableHrs',
      align: 'right',
      width: 130,
      render: (v) => (
        <Text strong style={{ color: '#1677ff' }}>{fmt(v)}</Text>
      ),
    },
    {
      title: 'Rate',
      dataIndex: 'BillableRate',
      key: 'BillableRate',
      align: 'right',
      width: 90,
      render: (v) => v != null ? fmtCurrency(v) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Est. Revenue',
      dataIndex: 'EstRevenue',
      key: 'EstRevenue',
      align: 'right',
      width: 120,
      render: (v) => v != null
        ? <Text strong style={{ color: '#52c41a' }}>{fmtCurrency(v)}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Notes',
      dataIndex: 'Notes',
      key: 'Notes',
      ellipsis: true,
      render: (v) => v || null,
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm
            title="Delete this forecast row?"
            onConfirm={() => handleDelete(row.ForecastID)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>FTE Forecasting</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          Add Forecast
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <DatePicker
          picker="month"
          value={selectedMonth ? dayjs(selectedMonth, 'YYYY-MM') : null}
          onChange={(d) => setSelectedMonth(d ? d.format('YYYY-MM') : '')}
          format="MMMM YYYY"
          allowClear={false}
          style={{ width: 180 }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="All teams"
          options={teamOptions}
          value={selectedTeams}
          onChange={setSelectedTeams}
          optionFilterProp="label"
          style={{ minWidth: 240 }}
          maxTagCount="responsive"
        />
      </Space>

      {rows.length > 0 && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Card size="small">
              <Statistic title="Total FTE" value={totals.fte} precision={2} />
            </Card>
          </Col>
          <Col>
            <Card size="small">
              <Statistic title="Est. Billable Hours" value={totals.hrs} precision={2} />
            </Card>
          </Col>
          {totals.hasRev && (
            <Col>
              <Card size="small">
                <Statistic
                  title="Est. Revenue"
                  value={totals.rev}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
          )}
        </Row>
      )}

      <Table
        rowKey="ForecastID"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        summary={() =>
          rows.length > 1 ? (
            <Table.Summary.Row style={{ fontWeight: 600, background: '#fafafa' }}>
              <Table.Summary.Cell index={0} colSpan={2}>Total</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">{fmt(totals.fte)}</Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                {fmt(rows.reduce((s, r) => s + (r.WeekdayHrs || 0), 0))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                {fmt(rows.reduce((s, r) => s + (r.WeekendHrs || 0), 0))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                {fmt(rows.reduce((s, r) => s + (r.HolidayHrs || 0), 0))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} />
              <Table.Summary.Cell index={7} align="right">
                <Text strong style={{ color: '#1677ff' }}>{fmt(totals.hrs)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} />
              <Table.Summary.Cell index={9} align="right">
                {totals.hasRev
                  ? <Text strong style={{ color: '#52c41a' }}>{fmtCurrency(totals.rev)}</Text>
                  : null}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10} colSpan={2} />
            </Table.Summary.Row>
          ) : null
        }
      />

      <Modal maskClosable={false}
        title={editing ? 'Edit Forecast' : 'Add Forecast'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="MonthKey"
                label="Month"
                rules={[{ required: true, message: 'Required' }]}
                getValueFromEvent={(d) => d ? d.format('YYYY-MM') : ''}
                getValueProps={(v) => ({ value: v ? dayjs(v, 'YYYY-MM') : null })}
              >
                <DatePicker picker="month" format="MMMM YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="FTE"
                label="FTE"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="e.g. 22.00" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="TeamID"
                label="Team"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  showSearch
                  placeholder="Select team"
                  options={teamOptions}
                  optionFilterProp="label"
                  disabled={!!editing}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="JobID"
                label="Job"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  showSearch
                  placeholder="Select job"
                  options={jobOptions}
                  optionFilterProp="label"
                  disabled={!!editing}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="WeekdayHrs" label="Weekday Hrs">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="WeekendHrs" label="Weekend Hrs">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="HolidayHrs" label="Holiday Hrs">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="AdherencePct" label="Adherence %">
                <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} placeholder="96.00" addonAfter="%" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="BillableRate" label="Billable Rate ($/hr)">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" addonBefore="$" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="Notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional notes" />
          </Form.Item>

          <Text type="secondary" style={{ fontSize: 12 }}>
            Est. Billable Hours = Weekday Hrs + Weekend Hrs + Holiday Hrs
          </Text>
        </Form>
      </Modal>
    </div>
  );
}
